/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronDown,
  Share2,
  Copy,
  Check,
  ExternalLink,
  AlertCircle,
  ArrowRight,
  BookOpen,
  Shield,
  FileText,
  Stethoscope,
  Building2,
  CalendarClock,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { SITE_URL } from "@/lib/seo";
import {
  MEDICATIONS,
  DIRECT_COMPARISONS,
  MEDICAL_DATA_VERSION,
  LAST_DATA_REVIEW_HUMAN_ES,
  findMedication,
  findDirectComparison,
  pairId,
} from "@/data/glp1-es";

/**
 * Comparador GLP-1 · AI Metabolic (mercado español)
 * Objetivo: educativo. No recomienda, no diagnostica, no prescribe.
 */

// ───────────────────────── Analytics helper ─────────────────────────
function track(event, params = {}) {
  const payload = { ...params, medical_data_version: MEDICAL_DATA_VERSION };
  // Preserve UTMs from URL if present
  try {
    const usp = new URLSearchParams(window.location.search);
    ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach((k) => {
      const v = usp.get(k);
      if (v) payload[k] = v;
    });
  } catch (_) { /* noop */ }
  // GA4 gtag
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", event, payload);
  }
}

// ───────────────────────── Selector ─────────────────────────
function TreatmentSelect({ slot, value, onChange, otherValue, testid }) {
  return (
    <div className="w-full">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#B4C5A4] font-sans-display font-semibold mb-2">
        Tratamiento {slot}
      </p>
      <div className="relative">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          data-testid={testid}
          className="w-full h-14 pl-4 pr-10 bg-white border-2 border-slate-200 rounded-lg text-[#0F172A] font-sans-display text-lg appearance-none cursor-pointer focus:outline-none focus:border-[#0B2545] transition"
        >
          <option value="">Seleccionar tratamiento</option>
          {MEDICATIONS.map((m) => (
            <option key={m.id} value={m.id} disabled={m.id === otherValue}>
              {m.brand_name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
      </div>
    </div>
  );
}

// ───────────────────────── Reusable UI blocks ─────────────────────────
function SectionRow({ icon: Icon, label, children }) {
  return (
    <div className="py-5 border-b border-slate-100 last:border-none grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 sm:gap-8">
      <div className="flex items-start gap-2">
        {Icon && <Icon className="h-4 w-4 text-[#B4C5A4] mt-0.5 flex-shrink-0" />}
        <p className="text-xs uppercase tracking-widest text-[#475569] font-sans-display font-semibold">
          {label}
        </p>
      </div>
      <div className="text-[#0F172A] text-[15px] leading-relaxed">{children}</div>
    </div>
  );
}

function TwoColRow({ label, icon: Icon, a, b }) {
  return (
    <div className="py-5 border-b border-slate-100 last:border-none">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4 text-[#B4C5A4] flex-shrink-0" />}
        <p className="text-xs uppercase tracking-widest text-[#475569] font-sans-display font-semibold">
          {label}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
        <div className="text-[#0F172A] text-[15px] leading-relaxed">{a}</div>
        <div className="text-[#0F172A] text-[15px] leading-relaxed">{b}</div>
      </div>
    </div>
  );
}

function Pending() {
  return <span className="italic text-slate-400">Pendiente de revisión</span>;
}

function SourceLink({ href, children, onClick }) {
  if (!href) return <Pending />;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545] transition"
    >
      {children}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

// ───────────────────────── Evidence · Direct head-to-head ─────────────────────────
function DirectEvidenceBlock({ direct, medA, medB, onExpand, onSourceOpened }) {
  // Order results to match displayed pair order (A then B)
  const rA = direct.results.find((r) => r.medication_id === medA.id);
  const rB = direct.results.find((r) => r.medication_id === medB.id);
  const values = [rA, rB].filter(Boolean).map((r) => Math.abs(r.weight_change_percent));
  const maxVal = Math.max(...values, 1);
  useEffect(() => {
    onExpand?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direct.trial]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8" data-testid="evidence-direct">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-sans-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#B4C5A4]/20 text-[#0B2545]">
          <Check className="h-3 w-3" />
          Comparación directa en ensayo clínico
        </span>
      </div>
      <h4 className="font-sans-display text-xl text-[#0B2545] tracking-tight mb-1">
        Resultado observado en {direct.trial}
      </h4>
      <p className="text-sm text-[#475569] leading-relaxed mb-6">
        {direct.design_summary_es}
      </p>

      {/* Common-axis bar chart — allowed only for direct head-to-head */}
      <div className="space-y-4 mb-6" data-testid="direct-chart">
        {[
          { med: medA, r: rA, color: "#0B2545" },
          { med: medB, r: rB, color: "#B4C5A4" },
        ].map(({ med, r, color }) =>
          r ? (
            <div key={med.id}>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="font-sans-display text-sm font-medium text-[#0B2545]">
                  {med.brand_name} <span className="text-slate-400 font-normal">· {r.arm}</span>
                </span>
                <span className="font-sans-display text-lg font-semibold text-[#0F172A]">
                  {r.weight_change_percent.toFixed(1)}%
                </span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${(Math.abs(r.weight_change_percent) / maxVal) * 100}%`,
                    background: color,
                  }}
                />
              </div>
            </div>
          ) : null,
        )}
      </div>

      <p className="text-xs italic text-[#475569] leading-relaxed border-l-2 border-[#B4C5A4] pl-3 mb-5">
        Comparación directa realizada en una población concreta y bajo las condiciones del ensayo. El resultado no determina qué tratamiento es adecuado para una persona concreta.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-[#475569]">
        <div>
          <span className="font-sans-display uppercase tracking-widest text-[10px] font-semibold text-[#B4C5A4]">Duración</span>
          <p className="text-[#0F172A]">{direct.duration_label_es}</p>
        </div>
        <div>
          <span className="font-sans-display uppercase tracking-widest text-[10px] font-semibold text-[#B4C5A4]">Población</span>
          <p className="text-[#0F172A]">{direct.population}</p>
        </div>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <SourceLink
          href={direct.reference}
          onClick={() =>
            track("glp1_source_opened", {
              treatment_id: null,
              source_type: "primary_trial",
              trial_name: direct.trial,
            })
          }
        >
          Ver publicación de {direct.trial}
        </SourceLink>
      </div>
    </div>
  );
}

// ───────────────────────── Evidence · Cross-trial (independent studies) ─────────────────────────
function CrossTrialCard({ med, onSourceOpened }) {
  const ev = med.pivotal_evidence;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`crosstrial-card-${med.id}`}>
      <p className="font-sans-display text-xs uppercase tracking-[0.2em] text-[#B4C5A4] font-semibold mb-2">
        {med.brand_name}
      </p>
      <h4 className="font-sans-display text-lg text-[#0B2545] tracking-tight mb-1">
        {ev.trial}
      </h4>
      <p className="text-xs text-[#475569] mb-4">{ev.duration_weeks} semanas</p>
      <div className="space-y-2 mb-4">
        {ev.results.map((r, i) => (
          <div key={i} className="flex items-baseline justify-between text-sm py-1 border-b border-slate-50 last:border-none">
            <span className="text-[#475569]">{r.arm}</span>
            <span className="font-sans-display font-semibold text-[#0F172A] tabular-nums">
              {r.weight_change_percent.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
      {ev.dose_note && (
        <p className="text-[11px] italic text-slate-500 leading-relaxed mb-3">{ev.dose_note}</p>
      )}
      <SourceLink
        href={ev.reference}
        onClick={() =>
          track("glp1_source_opened", {
            treatment_id: med.id,
            source_type: "primary_trial",
            trial_name: ev.trial,
          })
        }
      >
        Ver publicación de {ev.trial}
      </SourceLink>
    </div>
  );
}

function CrossTrialBlock({ medA, medB }) {
  return (
    <div data-testid="evidence-cross-trial">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-sans-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
          <AlertCircle className="h-3 w-3" />
          Evidencia procedente de estudios diferentes
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <CrossTrialCard med={medA} />
        <CrossTrialCard med={medB} />
      </div>

      <div className="bg-amber-50/60 border-l-4 border-amber-400 rounded-r-lg p-4 sm:p-5">
        <p className="text-sm text-[#0F172A] leading-relaxed">
          <strong className="font-sans-display">Estos resultados proceden de estudios diferentes y no constituyen una comparación directa.</strong>{" "}
          La población, duración, dosis, diseño y metodología de análisis pueden variar entre estudios.
        </p>
      </div>
    </div>
  );
}

// ───────────────────────── Comparison result ─────────────────────────
function ComparisonResult({ medA, medB }) {
  const direct = findDirectComparison(medA.id, medB.id);
  const evidenceMode = direct ? "direct_head_to_head" : "cross_trial";
  const pid = pairId(medA.id, medB.id);
  const renderedOnce = useRef(false);

  useEffect(() => {
    if (renderedOnce.current) return;
    renderedOnce.current = true;
    track("glp1_comparison_rendered", {
      pair_id: pid,
      evidence_mode: evidenceMode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  return (
    <section
      id="comparison-result"
      data-testid="comparison-result"
      className="mt-14 sm:mt-16"
    >
      {/* Header · brand names */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {[medA, medB].map((m) => (
          <div key={m.id} className="bg-white border-t-4 border-[#0B2545] rounded-b-xl px-6 py-5 shadow-sm">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#B4C5A4] font-sans-display font-semibold">
              {m.drug_class}
            </p>
            <h3 className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight mt-1">
              {m.brand_name}
            </h3>
            <p className="text-sm text-[#475569] italic mt-1">{m.active_ingredient}</p>
          </div>
        ))}
      </div>

      {/* Rows */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-9">
        <TwoColRow
          icon={FileText}
          label="Principio activo"
          a={<span>{medA.active_ingredient}</span>}
          b={<span>{medB.active_ingredient}</span>}
        />
        <TwoColRow
          icon={FileText}
          label="Mecanismo de acción"
          a={<span>{medA.drug_class}</span>}
          b={<span>{medB.drug_class}</span>}
        />
        <TwoColRow
          icon={FileText}
          label="Indicación autorizada (control del peso)"
          a={<span className="text-sm leading-relaxed">{medA.eu_indication_adults.summary}</span>}
          b={<span className="text-sm leading-relaxed">{medB.eu_indication_adults.summary}</span>}
        />
        <TwoColRow
          icon={Stethoscope}
          label="Receta médica"
          a={<span>Requiere receta: <strong className="text-[#0B2545]">Sí</strong></span>}
          b={<span>Requiere receta: <strong className="text-[#0B2545]">Sí</strong></span>}
        />
        <TwoColRow
          icon={CalendarClock}
          label="Administración"
          a={
            <div className="space-y-0.5">
              <p>Vía {medA.administration.route}</p>
              <p className="text-sm text-[#475569]">
                Frecuencia: <strong className="text-[#0F172A]">{medA.administration.frequency_label_es}</strong>
              </p>
            </div>
          }
          b={
            <div className="space-y-0.5">
              <p>Vía {medB.administration.route}</p>
              <p className="text-sm text-[#475569]">
                Frecuencia: <strong className="text-[#0F172A]">{medB.administration.frequency_label_es}</strong>
              </p>
            </div>
          }
        />
        <TwoColRow
          icon={Building2}
          label="Financiación en el SNS"
          a={
            <div>
              <p className="text-sm">No financiado por el SNS para control del peso.</p>
              <p className="text-xs italic text-slate-500 mt-1">Consultado el {LAST_DATA_REVIEW_HUMAN_ES}.</p>
              {medA.sns_funding.detail && medA.id === "mounjaro" && (
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{medA.sns_funding.detail}</p>
              )}
            </div>
          }
          b={
            <div>
              <p className="text-sm">No financiado por el SNS para control del peso.</p>
              <p className="text-xs italic text-slate-500 mt-1">Consultado el {LAST_DATA_REVIEW_HUMAN_ES}.</p>
              {medB.sns_funding.detail && medB.id === "mounjaro" && (
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{medB.sns_funding.detail}</p>
              )}
            </div>
          }
        />
        <TwoColRow
          icon={Shield}
          label="Estado en España"
          a={<span className="text-sm">{medA.spain_availability.display}</span>}
          b={<span className="text-sm">{medB.spain_availability.display}</span>}
        />

        {/* Evidence — mode dependent */}
        <div className="py-6 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-4 w-4 text-[#B4C5A4]" />
            <p className="text-xs uppercase tracking-widest text-[#475569] font-sans-display font-semibold">
              Evidencia clínica
            </p>
          </div>
          {direct ? (
            <DirectEvidenceBlock
              direct={direct}
              medA={medA}
              medB={medB}
              onExpand={() =>
                track("glp1_evidence_expanded", { pair_id: pid, trial_name: direct.trial })
              }
            />
          ) : (
            <CrossTrialBlock medA={medA} medB={medB} />
          )}
        </div>

        {/* Adverse effects (grouped, no cross-trial percentage comparison) */}
        <div className="py-6 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="h-4 w-4 text-[#B4C5A4]" />
            <p className="text-xs uppercase tracking-widest text-[#475569] font-sans-display font-semibold">
              Efectos adversos frecuentes
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {[medA, medB].map((m) => (
              <div key={m.id}>
                <p className="font-sans-display text-sm font-semibold text-[#0B2545] mb-2">
                  {m.brand_name}
                </p>
                <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">
                  {m.common_adverse_effects.frequency_band}
                </p>
                <ul className="space-y-1 text-sm text-[#475569]">
                  {m.common_adverse_effects.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="h-1 w-1 rounded-full bg-[#B4C5A4] mt-2 flex-shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-xs italic text-slate-500 mt-4 leading-relaxed">
            Categorías de frecuencia procedentes de las fichas técnicas. No se enfrentan porcentajes procedentes de ensayos diferentes.
          </p>
        </div>

        {/* Safety topics summary */}
        <details className="py-6 border-b border-slate-100 group" data-testid="safety-details">
          <summary
            className="cursor-pointer list-none flex items-center justify-between"
            onClick={(e) => {
              if (!e.currentTarget.parentElement.open) {
                track("glp1_safety_expanded", { treatment_id: `${medA.id}+${medB.id}` });
              }
            }}
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#B4C5A4]" />
              <p className="text-xs uppercase tracking-widest text-[#475569] font-sans-display font-semibold">
                Información de seguridad (resumen)
              </p>
            </div>
            <span className="text-2xl leading-none text-[#B4C5A4] group-open:rotate-45 transition-transform">+</span>
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mt-5">
            {[medA, medB].map((m) => (
              <div key={m.id}>
                <p className="font-sans-display text-sm font-semibold text-[#0B2545] mb-2">
                  {m.brand_name}
                </p>
                <ul className="space-y-1 text-sm text-[#475569]">
                  {m.key_safety_topics.map((it, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="h-1 w-1 rounded-full bg-[#B4C5A4] mt-2 flex-shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
                <div className="mt-3">
                  <SourceLink
                    href={m.sources.cima}
                    onClick={() =>
                      track("glp1_source_opened", { treatment_id: m.id, source_type: "cima" })
                    }
                  >
                    Consultar ficha técnica completa
                  </SourceLink>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs italic text-slate-500 mt-5 leading-relaxed">
            Resumen no exhaustivo. La ficha técnica completa contiene la información oficial y actualizada de seguridad.
          </p>
        </details>

        {/* MAH */}
        <TwoColRow
          icon={Building2}
          label="Titular de autorización"
          a={<span className="text-sm">{medA.marketing_authorisation_holder}</span>}
          b={<span className="text-sm">{medB.marketing_authorisation_holder}</span>}
        />

        {/* Sources */}
        <div className="py-6 border-b border-slate-100">
          <p className="text-xs uppercase tracking-widest text-[#475569] font-sans-display font-semibold mb-4">
            Fuentes y evidencia
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {[medA, medB].map((m) => (
              <div key={m.id}>
                <p className="font-sans-display text-sm font-semibold text-[#0B2545] mb-2">{m.brand_name}</p>
                <ul className="space-y-1.5">
                  {[
                    { label: "EMA", key: "ema" },
                    { label: "AEMPS · CIMA", key: "cima" },
                    { label: "BIFIMED (financiación)", key: "bifimed" },
                    { label: `Ensayo pivotal (${m.pivotal_evidence.trial})`, key: "primary_trial" },
                  ].map((s) => (
                    <li key={s.key}>
                      <SourceLink
                        href={m.sources[s.key]}
                        onClick={() =>
                          track("glp1_source_opened", {
                            treatment_id: m.id,
                            source_type: s.key,
                            trial_name: s.key === "primary_trial" ? m.pivotal_evidence.trial : null,
                          })
                        }
                      >
                        {s.label}
                      </SourceLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Last review */}
        <SectionRow icon={CalendarClock} label="Última revisión de datos">
          <span className="text-sm text-[#475569]">{LAST_DATA_REVIEW_HUMAN_ES}</span>
        </SectionRow>
      </div>
    </section>
  );
}

// ───────────────────────── Main page ─────────────────────────
export default function ComparadorGLP1() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const heroBtnRef = useRef(null);

  // Parse & sanitize URL params
  const validIds = MEDICATIONS.map((m) => m.id);
  const paramA = searchParams.get("tratamiento1");
  const paramB = searchParams.get("tratamiento2");
  const initA = validIds.includes(paramA) ? paramA : null;
  const initB = validIds.includes(paramB) && paramB !== initA ? paramB : null;

  const [treatmentA, setTreatmentA] = useState(initA);
  const [treatmentB, setTreatmentB] = useState(initB);
  // Committed pair (only after Compare is pressed OR from URL preload)
  const [committed, setCommitted] = useState(
    initA && initB && initA !== initB ? { a: initA, b: initB } : null,
  );

  const medA = committed ? findMedication(committed.a) : null;
  const medB = committed ? findMedication(committed.b) : null;

  const canCompare = treatmentA && treatmentB && treatmentA !== treatmentB;

  // Fire view event once on mount
  useEffect(() => {
    track("glp1_comparator_view", {});
  }, []);

  // Change tracking
  const handleSelect = useCallback(
    (slot, id) => {
      if (slot === "A") setTreatmentA(id);
      else setTreatmentB(id);
      if (id) track("glp1_treatment_selected", { slot, treatment_id: id });
    },
    [],
  );

  const smoothScrollToResult = () => {
    setTimeout(() => {
      const el = document.getElementById("comparison-result");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const doCompare = () => {
    if (!canCompare) return;
    const prevPair = committed ? pairId(committed.a, committed.b) : null;
    const newPair = pairId(treatmentA, treatmentB);
    setCommitted({ a: treatmentA, b: treatmentB });
    // Update URL without reload
    setSearchParams(
      (prev) => {
        prev.set("tratamiento1", treatmentA);
        prev.set("tratamiento2", treatmentB);
        return prev;
      },
      { replace: false },
    );
    track("glp1_compare_submit", {
      treatment_a: treatmentA,
      treatment_b: treatmentB,
      pair_id: newPair,
    });
    if (prevPair && prevPair !== newPair) {
      track("glp1_comparison_changed", {
        previous_pair: prevPair,
        new_pair: newPair,
      });
    }
    smoothScrollToResult();
  };

  const handleShare = async () => {
    if (!committed) return;
    const url = `${window.location.origin}/comparador-glp1?tratamiento1=${committed.a}&tratamiento2=${committed.b}`;
    const pid = pairId(committed.a, committed.b);
    track("glp1_share_click", { pair_id: pid });
    // Prefer native share if available (mobile), fallback to clipboard
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "Comparador GLP-1 · AI Metabolic", url });
        return;
      } catch (_) { /* user cancelled */ }
    }
    await handleCopy(url, pid);
  };

  const handleCopy = async (urlOverride, pidOverride) => {
    if (!committed) return;
    const url =
      urlOverride ||
      `${window.location.origin}/comparador-glp1?tratamiento1=${committed.a}&tratamiento2=${committed.b}`;
    const pid = pidOverride || pairId(committed.a, committed.b);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 1800);
      track("glp1_copy_link", { pair_id: pid });
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const scrollToSelector = () => {
    const el = document.getElementById("comparator");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Structured data — Article schema for medical/editorial content (no medical advice)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Comparador GLP-1 en España: Wegovy, Mounjaro y Saxenda",
    description:
      "Comparación educativa basada en fuentes oficiales (EMA, AEMPS, BIFIMED) de tratamientos GLP-1 y relacionados autorizados en España para el control del peso.",
    inLanguage: "es-ES",
    dateModified: "2026-09-11",
    publisher: {
      "@type": "Organization",
      name: "AI METABOLIC",
      url: SITE_URL,
    },
    mainEntityOfPage: `${SITE_URL}/comparador-glp1`,
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0F172A] font-body" data-testid="comparador-page">
      <SEO
        title="Comparador GLP-1 en España: Wegovy, Mounjaro y Saxenda | AI Metabolic"
        description="Compara Wegovy, Mounjaro y Saxenda en España: principio activo, mecanismo, indicación, frecuencia, evidencia clínica, seguridad y financiación. Información educativa basada en EMA y AEMPS."
        path="/comparador-glp1"
        structuredData={structuredData}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden pt-16 sm:pt-24 pb-14 sm:pb-20">
          <div className="pointer-events-none absolute -top-40 -right-32 h-96 w-96 rounded-full bg-[#B4C5A4]/20 blur-3xl" />
          <div className="max-w-5xl mx-auto px-6 lg:px-10 relative">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#B4C5A4] font-sans-display font-semibold mb-4">
              Herramienta educativa · España
            </p>
            <h1 className="font-sans-display text-3xl sm:text-4xl lg:text-[52px] text-[#0B2545] tracking-tight leading-[1.1] mb-6 max-w-3xl">
              Comparador de tratamientos GLP-1 y relacionados para el control del peso
            </h1>
            <p className="text-lg sm:text-xl text-[#0F172A] leading-relaxed max-w-2xl mb-3">
              Compara de forma sencilla las principales opciones utilizadas para el control del peso en España.
            </p>
            <p className="text-base text-[#475569] leading-relaxed max-w-2xl mb-8">
              Consulta sus diferencias en principio activo, mecanismo de acción, indicación autorizada, frecuencia de administración, evidencia clínica, efectos adversos y situación de financiación.
            </p>
            <p className="text-xs italic text-[#475569] max-w-2xl mb-8">
              Información educativa basada en fuentes oficiales y estudios clínicos. No sustituye la valoración de un profesional sanitario.
            </p>
            <button
              ref={heroBtnRef}
              type="button"
              onClick={scrollToSelector}
              data-testid="hero-scroll-cta"
              className="lift-btn inline-flex items-center gap-2 h-12 px-6 bg-[#0B2545] text-[#F9F8F6] font-sans-display font-medium tracking-wide rounded-md hover:bg-[#0a1f3a] transition"
            >
              Comparar tratamientos
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        </section>

        {/* Comparator selector */}
        <section id="comparator" className="pb-8">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-9 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-end">
                <TreatmentSelect
                  slot="A"
                  value={treatmentA}
                  otherValue={treatmentB}
                  onChange={(id) => handleSelect("A", id)}
                  testid="select-treatment-a"
                />
                <p className="text-center font-sans-display text-lg text-slate-400 italic pb-2 md:pb-4">vs</p>
                <TreatmentSelect
                  slot="B"
                  value={treatmentB}
                  otherValue={treatmentA}
                  onChange={(id) => handleSelect("B", id)}
                  testid="select-treatment-b"
                />
              </div>
              <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  type="button"
                  onClick={doCompare}
                  disabled={!canCompare}
                  data-testid="compare-button"
                  className="lift-btn flex-1 h-12 px-6 bg-[#0B2545] text-[#F9F8F6] font-sans-display font-medium tracking-wide rounded-md hover:bg-[#0a1f3a] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#0B2545] transition"
                >
                  Comparar
                </button>
                {committed && (
                  <>
                    <button
                      type="button"
                      onClick={handleShare}
                      data-testid="share-button"
                      className="inline-flex items-center justify-center gap-2 h-12 px-5 bg-white border border-slate-200 text-[#0B2545] rounded-md hover:border-[#0B2545] transition font-sans-display text-sm"
                    >
                      <Share2 className="h-4 w-4" />
                      Compartir comparación
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopy()}
                      data-testid="copy-link-button"
                      className="inline-flex items-center justify-center gap-2 h-12 px-5 bg-white border border-slate-200 text-[#0B2545] rounded-md hover:border-[#0B2545] transition font-sans-display text-sm"
                    >
                      {copied ? <Check className="h-4 w-4 text-[#B4C5A4]" /> : <Copy className="h-4 w-4" />}
                      {copied ? "Copiado" : "Copiar enlace"}
                    </button>
                  </>
                )}
              </div>
              {!canCompare && (
                <p className="mt-3 text-xs italic text-slate-400">
                  Selecciona dos tratamientos diferentes para activar la comparación.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Result */}
        {medA && medB && (
          <section className="pb-4">
            <div className="max-w-5xl mx-auto px-6 lg:px-10">
              <ComparisonResult medA={medA} medB={medB} />
            </div>
          </section>
        )}

        {/* Main disclaimer */}
        <section className="pt-14 pb-10">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <div
              className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8"
              data-testid="main-disclaimer"
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#B4C5A4] font-sans-display font-semibold mb-3">
                Información importante
              </p>
              <p className="text-sm text-[#475569] leading-relaxed">
                AI Metabolic ofrece información educativa y no sustituye el diagnóstico, tratamiento ni consejo de un profesional sanitario. AI Metabolic no prescribe ni recomienda un medicamento concreto. La elección y el seguimiento de un tratamiento requieren una valoración clínica individual. La información sobre medicamentos puede cambiar; consulta siempre la ficha técnica y el prospecto vigentes.
              </p>
              <p className="text-sm text-[#0B2545] leading-relaxed mt-3 font-sans-display font-semibold">
                Los medicamentos incluidos requieren receta médica.
              </p>
              <p className="text-xs italic text-slate-500 mt-4">
                Última revisión de datos: {LAST_DATA_REVIEW_HUMAN_ES}.
              </p>
            </div>
          </div>
        </section>

        {/* Post-comparison CTA */}
        <section className="pt-4 pb-24 sm:pb-32">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <div className="bg-[#0B2545] text-[#F9F8F6] rounded-2xl p-8 sm:p-12 relative overflow-hidden" data-testid="post-cta">
              <div className="pointer-events-none absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-[#B4C5A4]/15 blur-3xl" />
              <div className="relative max-w-2xl">
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#B4C5A4] font-sans-display font-semibold mb-3">
                  Siguiente paso
                </p>
                <h2 className="font-sans-display text-2xl sm:text-3xl tracking-tight leading-tight mb-4">
                  Los tratamientos pueden compararse. Tu situación necesita contexto.
                </h2>
                <p className="text-[#F9F8F6]/80 leading-relaxed mb-7">
                  Si estás valorando un tratamiento farmacológico para el sobrepeso o la obesidad, prepara la información y las preguntas que puede ser útil comentar con un profesional sanitario.
                </p>
                <Link
                  to="/es#final-cta"
                  onClick={() =>
                    track("glp1_prepare_consultation_click", {
                      pair_id: committed ? pairId(committed.a, committed.b) : null,
                      evidence_mode: committed
                        ? (findDirectComparison(committed.a, committed.b) ? "direct_head_to_head" : "cross_trial")
                        : null,
                    })
                  }
                  data-testid="prepare-consultation-button"
                  className="lift-btn inline-flex items-center gap-2 h-12 px-6 bg-[#B4C5A4] text-[#0B2545] font-sans-display font-medium tracking-wide rounded-md hover:bg-[#a3b793] transition"
                >
                  Preparar mi consulta
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="text-xs text-[#F9F8F6]/60 mt-4 italic">
                  2 minutos · Gratuito · No sustituye una valoración médica
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
